import { ActionType, createReducer } from "typesafe-actions";

import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import { EntityMention } from "@app/component/EntityMentions";
import CoachingNote from "@app/scene/dashboard/types/CoachingNote";
import PendingShareEntity from "@app/scene/dashboard/types/PendingShareEntity";

import { deleteCoachingNoteCategory } from "../categories/actions";
import { createCoachingNoteWithTask } from "../noteWithTask/actions";
import { createCoachingTask } from "../tasks/actions";

import {
  bumpNotesRefreshCounter,
  clearPendingShare,
  CoachingNotesActions,
  createCoachingNote,
  createReply,
  deleteCoachingNote,
  fetchCoachingNotes,
  fetchReplies,
  setCategoryFilter,
  shareInChat,
  updateCoachingNote,
} from "./actions";
import reconcileLiveList from "./reconcileLiveList";

type NotesReducerActions =
  | ActionType<typeof createCoachingNoteWithTask>
  | ActionType<typeof createCoachingTask>
  | ActionType<typeof deleteCoachingNoteCategory>
  | CoachingNotesActions;

export interface CoachingNotesState {
  categoryFilter: number | undefined;
  createLoading: boolean;
  deleteLoadingIds: Array<CoachingNote["id"]>;
  error: unknown;
  hasMore: boolean;
  loading: boolean;
  notes: CoachingNote[];
  offset: number;
  pendingShareChartIds?: number[];
  pendingShareEntities: PendingShareEntity[];
  pendingShareMentions?: EntityMention[];
  pendingShareNote?: string;
  pendingShareUserId?: number;
  // Tactical: used as an event-bus to retrigger the panel's fetch useEffect
  // when the notifications-polling saga sees a new coaching event. We chose
  // this over lifting the panel-local filter / search / category state into
  // Redux so the saga could dispatch the fetch directly — TODO followup to
  // lift that state and dispatch the fetch action straight from the saga.
  refreshCounter: number;
  repliesByParent: Record<CoachingNote["id"], CoachingNote[]>;
  repliesCreatingByParent: Record<CoachingNote["id"], boolean>;
  repliesErrorByParent: Record<CoachingNote["id"], unknown>;
  repliesHasMoreByParent: Record<CoachingNote["id"], boolean>;
  repliesLoadingByParent: Record<CoachingNote["id"], boolean>;
  repliesOffsetByParent: Record<CoachingNote["id"], number>;
  updateLoadingIds: Array<CoachingNote["id"]>;
}

export const initialCoachingNotesState: CoachingNotesState = {
  categoryFilter: undefined,
  createLoading: false,
  deleteLoadingIds: [],
  error: undefined,
  hasMore: false,
  loading: false,
  notes: [],
  offset: 0,
  pendingShareEntities: [],
  refreshCounter: 0,
  repliesByParent: {},
  repliesCreatingByParent: {},
  repliesErrorByParent: {},
  repliesHasMoreByParent: {},
  repliesLoadingByParent: {},
  repliesOffsetByParent: {},
  updateLoadingIds: [],
};

const coachingNotes = createReducer<CoachingNotesState, NotesReducerActions>(
  initialCoachingNotesState
)
  .handleAction(fetchCoachingNotes.request, (state, { payload }) => ({
    ...state,
    error: undefined,
    hasMore: payload.silent ? state.hasMore : payload.append ? state.hasMore : false,
    loading: payload.silent ? state.loading : true,
    notes: payload.silent || payload.append ? state.notes : [],
    offset: payload.silent ? state.offset : payload.append ? state.offset : 0,
  }))
  .handleAction(fetchCoachingNotes.success, (state, { payload }) => {
    // Silent poll re-syncs the whole loaded window (the panel asks for offset 0,
    // limit = loaded count), so `payload.notes` is the server's current truth for
    // it — new notes, edits AND deletes all reflect. reconcileLiveList keeps any
    // in-flight optimistic temps and the deeper tail beyond the re-synced window.
    const notes = payload.append
      ? mergeNotes(state.notes, payload.notes)
      : payload.silent
        ? reconcileLiveList(payload.notes, state.notes, payload.hasMore)
        : payload.notes;
    return {
      ...state,
      hasMore: payload.hasMore,
      loading: false,
      notes,
      offset: payload.silent ? notes.length : payload.offset + payload.notes.length,
    };
  })
  .handleAction(fetchCoachingNotes.failure, (state, { payload }) => ({
    ...state,
    error: payload,
    loading: false,
  }))
  .handleAction(createCoachingNote.request, (state, { payload }) => {
    const tempNote: CoachingNote = {
      id: payload.tempId,
      answers: payload.answers,
      attachments: payload.attachments,
      categoryId: payload.categoryId ?? null,
      createdAt: payload.tempCreatedAt,
      createdBy: payload.tempAuthor.id,
      mentions: payload.mentions,
      note: payload.note,
      parentNoteId: null,
      subjectUserId: payload.subjectUserId,
      updatedAt: payload.tempCreatedAt,
      user: payload.tempAuthor,
      visibility: payload.visibility,
    };
    return {
      ...state,
      createLoading: true,
      notes: [tempNote, ...state.notes],
    };
  })
  .handleAction(createCoachingNote.success, (state, { payload }) => {
    const tempNote = state.notes.find((note) => note.id === payload.tempId);
    // Anonymous mode no-ops the write server-side (200 with an empty body, no id).
    // Keep the fully-formed optimistic temp note — its negative id survives the
    // live-poll reconcile — instead of swapping in the id-less response, which
    // crashes the feed render. Mirrors the createReply.success no-op guard.
    if (!isNotEmpty(payload.note.id)) {
      return { ...state, createLoading: false };
    }
    return {
      ...state,
      createLoading: false,
      // If the temp note was evicted from state (e.g., a filter change refetched
      // and replaced notes), fall back to prepending the server note so the
      // user's created note still lands in the feed.
      notes: tempNote
        ? state.notes.map((note) => (note.id === payload.tempId ? payload.note : note))
        : [payload.note, ...state.notes],
    };
  })
  .handleAction(createCoachingNote.failure, (state, { payload }) => ({
    ...state,
    createLoading: false,
    notes: state.notes.filter((note) => note.id !== payload.tempId),
  }))
  // The orchestrator saga creates the note first, then the task. The notes
  // server response on create doesn't include linkedTaskIds (only listing
  // does), so without this handler the "Task created" chip wouldn't appear
  // until the next list refetch. Splice the new task id onto the matching
  // note so the chip renders immediately. Dedup in case a refetch races and
  // hydrates linkedTaskIds before this handler runs.
  .handleAction(createCoachingNoteWithTask.success, (state, { payload }) => ({
    ...state,
    notes: state.notes.map((note) =>
      note.id === payload.note.id
        ? {
            ...note,
            linkedTaskIds: (note.linkedTaskIds ?? []).includes(payload.task.id)
              ? note.linkedTaskIds
              : [...(note.linkedTaskIds ?? []), payload.task.id],
          }
        : note
    ),
  }))

  .handleAction(createCoachingTask.success, (state, { payload }) => {
    if (!isNotEmpty(payload.coachingNoteId)) {
      return state;
    }
    return {
      ...state,
      notes: state.notes.map((note) =>
        note.id === payload.coachingNoteId
          ? {
              ...note,
              linkedTaskIds: (note.linkedTaskIds ?? []).includes(payload.id)
                ? note.linkedTaskIds
                : [...(note.linkedTaskIds ?? []), payload.id],
            }
          : note
      ),
    };
  })
  .handleAction(updateCoachingNote.request, (state, { payload }) => ({
    ...state,
    updateLoadingIds: [...state.updateLoadingIds, payload.id],
  }))
  .handleAction(updateCoachingNote.success, (state, { payload }) => {
    const updateLoadingIds = state.updateLoadingIds.filter((id) => id !== payload.id);
    if (!isNotEmpty(payload.parentNoteId)) {
      // Server only cascades visibility/categoryId to replies, so only drop the
      // cache when one of those actually changed — a plain text edit shouldn't
      // force the next expand to refetch.
      const prev = state.notes.find((note) => note.id === payload.id);
      const cascadeChanged =
        prev !== undefined &&
        (prev.visibility !== payload.visibility || prev.categoryId !== payload.categoryId);
      let { repliesByParent } = state;
      if (cascadeChanged) {
        const { [payload.id]: _droppedReplies, ...rest } = state.repliesByParent;
        repliesByParent = rest;
      }
      return {
        ...state,
        notes: state.notes.map((note) =>
          note.id === payload.id
            ? { ...payload, linkedTaskIds: payload.linkedTaskIds ?? note.linkedTaskIds }
            : note
        ),
        repliesByParent,
        updateLoadingIds,
      };
    }
    const parentId = payload.parentNoteId;
    return {
      ...state,
      repliesByParent: {
        ...state.repliesByParent,
        [parentId]: (state.repliesByParent[parentId] ?? []).map((reply) =>
          reply.id === payload.id ? payload : reply
        ),
      },
      updateLoadingIds,
    };
  })
  .handleAction(updateCoachingNote.failure, (state, { payload }) => ({
    ...state,
    updateLoadingIds: state.updateLoadingIds.filter((id) => id !== payload),
  }))
  .handleAction(deleteCoachingNote.request, (state, { payload }) => ({
    ...state,
    deleteLoadingIds: [...state.deleteLoadingIds, payload.noteId],
  }))
  .handleAction(deleteCoachingNote.success, (state, { payload }) => {
    const deleteLoadingIds = state.deleteLoadingIds.filter((id) => id !== payload.noteId);
    if (!isNotEmpty(payload.parentNoteId)) {
      // Top-level delete cascades server-side; drop any cached replies under it.
      const { [payload.noteId]: _droppedReplies, ...repliesByParent } = state.repliesByParent;
      const { [payload.noteId]: _droppedLoading, ...repliesLoadingByParent } =
        state.repliesLoadingByParent;
      const { [payload.noteId]: _droppedError, ...repliesErrorByParent } =
        state.repliesErrorByParent;
      const { [payload.noteId]: _droppedCreating, ...repliesCreatingByParent } =
        state.repliesCreatingByParent;
      const { [payload.noteId]: _droppedHasMore, ...repliesHasMoreByParent } =
        state.repliesHasMoreByParent;
      const { [payload.noteId]: _droppedOffset, ...repliesOffsetByParent } =
        state.repliesOffsetByParent;
      return {
        ...state,
        deleteLoadingIds,
        notes: state.notes.filter((note) => note.id !== payload.noteId),
        repliesByParent,
        repliesCreatingByParent,
        repliesErrorByParent,
        repliesHasMoreByParent,
        repliesLoadingByParent,
        repliesOffsetByParent,
      };
    }
    const parentId = payload.parentNoteId;
    return {
      ...state,
      deleteLoadingIds,
      notes: state.notes.map((note) =>
        note.id === parentId
          ? { ...note, replyCount: Math.max(0, (note.replyCount ?? 1) - 1) }
          : note
      ),
      repliesByParent: {
        ...state.repliesByParent,
        [parentId]: (state.repliesByParent[parentId] ?? []).filter(
          (reply) => reply.id !== payload.noteId
        ),
      },
    };
  })
  .handleAction(deleteCoachingNote.failure, (state, { payload }) => ({
    ...state,
    deleteLoadingIds: state.deleteLoadingIds.filter((id) => id !== payload),
  }))
  .handleAction(fetchReplies.request, (state, { payload }) => {
    const { append, parentNoteId, silent } = payload;
    const { [parentNoteId]: _clearedError, ...repliesErrorByParent } = state.repliesErrorByParent;
    const prevHasMore = state.repliesHasMoreByParent[parentNoteId] ?? false;
    const prevOffset = state.repliesOffsetByParent[parentNoteId] ?? 0;
    return {
      ...state,
      repliesErrorByParent,
      repliesHasMoreByParent: {
        ...state.repliesHasMoreByParent,
        [parentNoteId]: append ? prevHasMore : false,
      },
      // A silent poll must not flash the "Loading more..." indicator.
      repliesLoadingByParent: silent
        ? state.repliesLoadingByParent
        : { ...state.repliesLoadingByParent, [parentNoteId]: true },
      repliesOffsetByParent: {
        ...state.repliesOffsetByParent,
        [parentNoteId]: append ? prevOffset : 0,
      },
    };
  })
  .handleAction(fetchReplies.success, (state, { payload }) => {
    const { append, hasMore, offset, parentNoteId, replies, silent } = payload;
    const existing = state.repliesByParent[parentNoteId] ?? [];
    return {
      ...state,
      repliesByParent: {
        ...state.repliesByParent,
        // Silent poll re-syncs from offset 0 over the loaded replies plus a
        // page of headroom (replies are createdAt-ASC, so new ones land at the
        // tail). That window fully covers what's loaded, so `fresh` is the
        // authoritative truth — reply edits and deletes reflect, not just new
        // replies. hasMore=false: drop everything `fresh` omits except in-flight
        // optimistic temps (preserved by reconcileLiveList).
        [parentNoteId]: silent
          ? reconcileLiveList(replies, existing, false)
          : append
            ? mergeNotes(existing, replies)
            : replies,
      },
      repliesHasMoreByParent: { ...state.repliesHasMoreByParent, [parentNoteId]: hasMore },
      repliesLoadingByParent: { ...state.repliesLoadingByParent, [parentNoteId]: false },
      repliesOffsetByParent: {
        ...state.repliesOffsetByParent,
        [parentNoteId]: offset + replies.length,
      },
    };
  })
  .handleAction(fetchReplies.failure, (state, { payload }) => ({
    ...state,
    repliesErrorByParent: { ...state.repliesErrorByParent, [payload.parentNoteId]: payload.error },
    // Hide the sentinel after a failure — otherwise IntersectionObserver would
    // immediately re-fire once the error state clears, hammering a flaky endpoint.
    repliesHasMoreByParent: { ...state.repliesHasMoreByParent, [payload.parentNoteId]: false },
    repliesLoadingByParent: { ...state.repliesLoadingByParent, [payload.parentNoteId]: false },
  }))
  .handleAction(createReply.request, (state, { payload }) => {
    const parent = state.notes.find((note) => note.id === payload.parentNoteId);
    const tempReply: CoachingNote = {
      id: payload.tempId,
      answers: null,
      attachments: payload.attachments,
      categoryId: parent?.categoryId ?? null,
      createdAt: payload.tempCreatedAt,
      createdBy: payload.tempAuthor.id,
      mentions: payload.mentions,
      note: payload.note,
      parentNoteId: payload.parentNoteId,
      subjectUserId: payload.subjectUserId,
      updatedAt: payload.tempCreatedAt,
      user: payload.tempAuthor,
      visibility: payload.parentVisibility,
    };
    return {
      ...state,
      notes: state.notes.map((note) =>
        note.id === payload.parentNoteId
          ? { ...note, replyCount: (note.replyCount ?? 0) + 1 }
          : note
      ),
      repliesByParent: {
        ...state.repliesByParent,
        [payload.parentNoteId]: [...(state.repliesByParent[payload.parentNoteId] ?? []), tempReply],
      },
      repliesCreatingByParent: {
        ...state.repliesCreatingByParent,
        [payload.parentNoteId]: true,
      },
    };
  })
  .handleAction(createReply.success, (state, { payload }) => {
    const parentId = payload.note.parentNoteId;
    if (!isNotEmpty(parentId)) {
      return state;
    }
    return {
      ...state,
      repliesByParent: {
        ...state.repliesByParent,
        [parentId]: (state.repliesByParent[parentId] ?? []).map((reply) =>
          reply.id === payload.tempId ? payload.note : reply
        ),
      },
      repliesCreatingByParent: { ...state.repliesCreatingByParent, [parentId]: false },
    };
  })
  .handleAction(createReply.failure, (state, { payload }) => ({
    ...state,
    notes: state.notes.map((note) =>
      note.id === payload.parentNoteId
        ? { ...note, replyCount: Math.max(0, (note.replyCount ?? 1) - 1) }
        : note
    ),
    repliesByParent: {
      ...state.repliesByParent,
      [payload.parentNoteId]: (state.repliesByParent[payload.parentNoteId] ?? []).filter(
        (reply) => reply.id !== payload.tempId
      ),
    },
    repliesCreatingByParent: {
      ...state.repliesCreatingByParent,
      [payload.parentNoteId]: false,
    },
  }))
  .handleAction(setCategoryFilter, (state, { payload }) => ({
    ...state,
    categoryFilter: payload,
  }))
  .handleAction(shareInChat, (state, { payload }) => ({
    ...state,
    pendingShareChartIds: payload.chartIds,
    pendingShareEntities: payload.entities,
    pendingShareMentions: payload.mentions,
    pendingShareNote: payload.note,
    pendingShareUserId: payload.targetUserId,
  }))
  .handleAction(clearPendingShare, (state) => ({
    ...state,
    pendingShareChartIds: undefined,
    pendingShareEntities: [],
    pendingShareMentions: undefined,
    pendingShareNote: undefined,
    pendingShareUserId: undefined,
  }))
  .handleAction(bumpNotesRefreshCounter, (state) => ({
    ...state,
    refreshCounter: state.refreshCounter + 1,
  }))
  // A category can only be deleted once no notes use it, so there's nothing to
  // reassign — just drop the filter if it pointed at the deleted category.
  .handleAction(deleteCoachingNoteCategory.success, (state, { payload }) =>
    state.categoryFilter === payload.categoryId ? { ...state, categoryFilter: undefined } : state
  );

function mergeNotes(existing: CoachingNote[], incoming: CoachingNote[]): CoachingNote[] {
  const seen = new Set(existing.map((note) => note.id));
  return [...existing, ...incoming.filter((note) => !seen.has(note.id))];
}

export default coachingNotes;
